/* eslint-disable standard/computed-property-even-spacing */
import moment from 'moment-timezone'
import Base from '@/resources/Base'
import Recommendation from '@/resources/models/Recommendation'
import { getHostTrailRiskLevel, getDetectionRiskLevel, runningScoreContribution } from '@/utilities/riskscore'
import { buildAttackChainCommand } from '@/utilities/attack-chain.helper'
import { NANOSECONDS_TO_MILLISECONDS } from '@/constants/datetime.constants'
import { formatFromNow, formatTimeDistance, dateTimeSort } from '@/utilities/datetime.helper'
import { trailsDetailMetaInfo } from '@/column-header/trail/trail-details-meta'
import { getUserFriendlyTrailLabel } from '@/utilities/trail.helper'
import { getUserFriendlyAgentLabel } from '@/utilities/agent.helper'
import { handleNulls } from '@/utilities/sort.helper'
import { OrderByEnum } from '@/constants/orderby.constants'
import { MITRE_TECHNIQUE_URL } from '../../constants/attack-chain.constants'

const TRAILSCOREHISTORY_SIZE = 100

const addIocHostColumn = function (columns, label, position, parentHeader = '') {
  columns.splice(position, 0, {
    datatype: 'iocpoint',
    display: 'hostTarget',
    description: 'Host Affected',
    label: label,
    parentHeader: parentHeader, // used for container, will map to hostname
    sortable: false,
    'min-width': 70,
    positionIndex: position,
    name: 'host'
  })
}

export default class Trail extends Base {
  constructor (uuid = null, attributes = {}, options = {}) {
    super(uuid, attributes, options)

    // initial visible state is true
    this.privilege = false
    this.visible = true
    this.hostPath = {} // table details columns (score, time detected + host(s))
    this.trailSummary = {}
    this.recommendations = [] // Prepared list of IoC recommendation model objects
    this.influencedBy = [] // IoCs contributed to from other trails. maps to response influenceeList
    this.influences = [] // IoCs this trail has contributed to in other trails. maps to response influencerList
    this.collapsedTrailIocList = []
    this.collapsedView = true
    this.orderByDir = OrderByEnum.ASC
    this.jiraTicketLink = 'N/A'
    this.mitreAttackTactics = []
    // cloud trail flag for displaying Hosts or Roles in details page
    if (_.isUndefined(this.cloudNative)) {
      this.cloudNative = false
    }

    // for holding trail  ioc/detection paging
    this.currentPage = 0
    this.lastPage = 0
    this.nextPage = 1
    this.recordsPerPage = this.options.recordsPerPage || 25

    /**
     * handles state of vue-infinite-loading
     * 'loading' = more data to be loaded
     * 'complete' = all data has been loaded
     * 'reset' = init state
     */
    this.virtualState = undefined

    // convert startTime to ms if necessary
    if (this.startTime && !moment(this.startTime).isValid()) {
      this.startTime = this.startTime * NANOSECONDS_TO_MILLISECONDS
    }

    if (this.mitigateTime && !moment(this.mitigateTime).isValid()) {
      this.mitigateTime = this.mitigateTime * NANOSECONDS_TO_MILLISECONDS
    }

    // if lastMitigatedTime is set to a non zero number, trail is reopened
    if (this.state === 'ACTIVE' && this.lastMitigatedTime && this.lastMitigatedTime > 0) {
      this.state = 'REOPENED'
    }

    if (this.state !== 'MITIGATED') {
      this.dwellTime = formatFromNow(this.startTime, {
        years: { enabled: true, unit: 'y' },
        months: { enabled: true, unit: 'M' },
        days: { enabled: true, unit: 'd' },
        hours: { enabled: true, unit: 'h' },
        minutes: { enabled: true, unit: 'm' },
        seconds: { enabled: false, unit: 's' }
      })
    } else {
      this.dwellTime = formatTimeDistance(this.startTime, this.mitigateTime, {
        years: { enabled: true, unit: 'y' },
        months: { enabled: true, unit: 'M' },
        days: { enabled: true, unit: 'd' },
        hours: { enabled: true, unit: 'h' },
        minutes: { enabled: true, unit: 'm' },
        seconds: { enabled: false, unit: 's' }
      })
    }

    // convert startTime to ms if necessary
    if (this.lastIocSeenTime && !moment(this.lastIocSeenTime).isValid()) {
      this.lastIocSeenTime = this.lastIocSeenTime * NANOSECONDS_TO_MILLISECONDS
    }

    if (_.isUndefined(this.hostTimeInfoMap) || _.isNull(this.hostTimeInfoMap)) {
      this.hostTimeInfoMap = []
    } else {
      // if hostTimeInfoMap comes in as a map, convert to desc sort list
      if (!_.isArray(this.hostTimeInfoMap)) {
        let tempList = []
        Object.keys(this.hostTimeInfoMap).forEach(host => {
          tempList.push({
            host: host,
            seenTime: this.hostTimeInfoMap[host],
            dwellTime: formatFromNow(
              moment(this.hostTimeInfoMap[host]).isValid()
                ? this.hostTimeInfoMap[host]
                : this.hostTimeInfoMap[host] * NANOSECONDS_TO_MILLISECONDS,
              {
                years: { enabled: true, unit: 'y' },
                months: { enabled: true, unit: 'M' },
                days: { enabled: true, unit: 'd' },
                hours: { enabled: true, unit: 'h' },
                minutes: { enabled: true, unit: 'm' },
                seconds: { enabled: false, unit: 's' }
              }
            )
          })
        })
        this.hostTimeInfoMap = tempList.sort((a, b) => {
          return dateTimeSort(a.seenTime, b.seenTime)
        })
      }
    }

    // set trail work owner if exists
    if (this.ownerId && !_.isEmpty(this.ownerId)) {
      this.setOwnerId(this.ownerId)
    } else {
      this.ownerId = {
        firstname: '',
        lastname: '',
        email: '',
        fullname: 'Unassigned'
      }
    }

    this.labelTrail()
  }

  labelTrail () {
    this.id = this.trailIdHash
    this.trailIdLabel = this.createTrailLabel(this.attackId || this.trailIdHash)

    if (_.isNumber(this.riskScore)) {
      this.riskLevel = getHostTrailRiskLevel(this.riskScore)
    }
    if (!_.isEmpty(this.fingerprint)) {
      this.fingerprintLabel = this.fingerprint.substring(0, 12)
    }
  }

  setOwnerId (owner) {
    this.ownerId = owner
    this.ownerId.fullname = `${this.ownerId.firstname} ${this.ownerId.lastname}`
  }

  setCollapsedView (val) {
    this.collapsedView = val
  }

  updateWorkHistory (workHistory) {
    this.workHistory = workHistory
    if (this.workHistory && !_.isEmpty(this.workHistory)) {
      this.setOwnerId(this.workHistory[0].owner_user)
    }
  }

  createTrailLabel (label) {
    return getUserFriendlyTrailLabel(label)
  }

  createAgentName (label) {
    return getUserFriendlyAgentLabel(label)
  }

  setDetectionsPage (page) {
    this.currentPage = page
    this.lastPage = page !== 0 ? page - 1 : 0
    this.nextPage = page + 1
  }

  hasMoreDetections (page) {
    let iocListInView = this.trailIocList
    if (this.collapsedView) {
      iocListInView = this.collapsedTrailIocList
    }
    let nextPageFirstRecord = page * this.recordsPerPage + 1
    if (nextPageFirstRecord <= iocListInView.length) {
      return true
    }
    return false
  }

  getNextDetectionsPage (page) {
    let iocListInView = this.trailIocList
    if (this.collapsedView) {
      iocListInView = this.collapsedTrailIocList
    }
    let nextPageRecords = page * this.recordsPerPage
    if (nextPageRecords < iocListInView.length) {
      return iocListInView.slice(nextPageRecords, (page + 1) * this.recordsPerPage)
    }
    return iocListInView.slice(nextPageRecords, iocListInView.length)
  }

  getDetectionsCount () {
    return this.trailIocList.length
  }

  getCollapsedDetectionsCount () {
    return this.collapsedTrailIocList.length
  }

  /**
   * For collapsing scenario,
   * Detections are equal if agentId, ruleId, and iocSummary all match.
   */
  detectionEquals (detection1, detection2) {
    return (
      !detection1.lateralmove &&
      !detection2.lateralmove &&
      detection1.agentId === detection2.agentId &&
      detection1.containerId === detection2.containerId &&
      detection1.ruleid === detection2.ruleid &&
      detection1.iocSummary === detection2.iocSummary &&
      detection1.scoreContribution === detection2.scoreContribution
    )
  }

  /**
   * For collapsing scenario,
   * Lateral movements are equal if source agent, target agent,
   * sender process (lmEventList[0]) and recipient parent process (lmEventList[1]) all match.
   *
   * @param {*} latmove1
   * @param {*} latmove2
   */
  lateralMovementEquals (latmove1, latmove2) {
    return (
      latmove1.lateralmove &&
      latmove2.lateralmove &&
      latmove1.sourceAgent === latmove2.sourceAgent &&
      latmove1.targetAgent === latmove2.targetAgent &&
      latmove1.lmEventList[0].sourceProcessName === latmove2.lmEventList[0].sourceProcessName
    )
  }

  /**
   *
   * Configure kill chain chart settings and kill chain segment blocks.
   */
  configureKillChainFormat () {
    let timeFormat = moment(this.startTime).fromNow(true)
    let chains = {}
    if (this.trailTacticSet) {
      this.trailTacticSet.forEach((item, idx) => {
        this.trailTacticSet[idx] = this.trailTacticSet[idx].toUpperCase().replace(/ /g, '_')
      })
      chains = buildAttackChainCommand(this.trailTacticSet)
    } else if (this.iocTactic) {
      chains = buildAttackChainCommand([this.iocTactic.replace(/ /g, '_')])
    } else {
      chains = buildAttackChainCommand([])
    }
    this.killChainData = {
      centerDonutTitle: timeFormat.split(' '),
      datasets: [
        {
          data: chains['data'],
          backgroundColor: chains['backgroundColor']
        }
      ],
      labels: chains['labels']
    }
    this.mitreAttackTactics = {
      collapsedTrailIocList: this.trailTacticSet.map((tacticSet, index) => {
        return {
          rule: {
            externalTechniqueUrl: `${MITRE_TECHNIQUE_URL}${
              this.trailTechniqueSet[index] ? this.trailTechniqueSet[index].replace('.', '/') : '#'
            }`
          },
          iocTactic: tacticSet.toUpperCase().replace('_', ' ')
        }
      })
    }
  }

  /**
   *
   * Configure trail historical dataset. (x,y) => (seenTime, scoreContribution)
   * UI-204: trailRiskHistInfoList is deprecated
   *
   */
  configureRiskScoreHistory () {
    // compute running total
    _.map(this.trailRiskHistInfoList, (riskScore, idx) => {
      if (idx === 0) {
        riskScore.scoreAccumulation = riskScore.scoreContribution
      } else {
        if (!riskScore.scoreContribution) {
          riskScore.scoreContribution = 0
        }
        riskScore.scoreAccumulation =
          this.trailRiskHistInfoList[idx - 1]['scoreAccumulation'] + riskScore.scoreContribution
      }
    })

    this.riskScoreHistory = {
      labels: this.trailRiskHistInfoList.map(point => point.seenTime),
      datasets: [
        {
          data: this.trailRiskHistInfoList.map(point => point.scoreAccumulation)
        }
      ]
    }
  }

  /**
   *
   * Add or update trail that influenced this trail (influencedBy)
   */
  addInfluencedByTrail (trail) {
    let foundTrailIdx = -1
    // only storing top 10 influenced by trails
    if (this.influenceeList) {
      // top 10 comes from this.influenceeList,
      // exit if trail does not exists
      foundTrailIdx = _.findIndex(this.influenceeList, influence => {
        return influence === trail.id || influence.id === trail.id
      })
      if (foundTrailIdx < 0) {
        return false
      }
    }

    let influencedByTrail = _.find(this.influencedBy, { id: trail.id })
    if (_.isUndefined(influencedByTrail)) {
      trail.count = 1

      if (foundTrailIdx === -1) {
        this.influencedBy.push(trail)
      } else {
        if (_.isString(this.influenceeList[foundTrailIdx])) {
          this.influencedBy.push(_.merge(trail, { id: this.influenceeList[foundTrailIdx] }))
        } else {
          this.influencedBy.push(_.merge(trail, this.influenceeList[foundTrailIdx]))
        }
      }
      return trail
    }
    influencedByTrail.count++
    _.merge(influencedByTrail, trail)

    return influencedByTrail
  }

  /**
   *
   * InfluencedBy and Influences list sort
   */
  sortInfluencesTrail () {
    this.influencedBy.sort((a, b) => {
      return this.sortInfluencesFn(a, b)
    })

    this.influences.sort((a, b) => {
      return this.sortInfluencesFn(a, b)
    })
  }

  /**
   *
   * InfluencedBy and Influences list sorted by
   * 1. Longest dwell time
   * 2. Risk Score
   */
  sortInfluencesFn (a, b) {
    let nullSeenTime = handleNulls(a.startTime, b.startTime)
    // if either or both seentime is undefined or empty, return compare val
    if (nullSeenTime !== null) {
      return nullSeenTime
    }
    let nullScore = handleNulls(a.riskScore, b.riskScore)
    // if either or both scores are undefined or empty, return seentime compare
    if (nullScore !== null) {
      return a.startTime - b.startTime
    }
    // No value is undefined or empty, compare starttime then riskscore
    // a.startTime - b.startTime = 0 is equals, then compare score
    return a.startTime - b.startTime || a.riskScore - b.riskScore
  }

  updateInfluenceeList (influenceeList = [], allTrails) {
    this.updateInfluencesList(this.influenceeList, influenceeList, allTrails)
  }

  /**
   *
   * API gives back top 10 trails this trail influenced.
   * Update this trail to link to those Trail objects (allTrails).
   *
   * @param {*} influencerList String list of trail ids given by backend
   * @param {*} allTrails
   */
  updateInfluencerList (influencerList = [], allTrails) {
    for (let idx = 0; idx < influencerList.length; idx++) {
      let influenceTrail = _.find(allTrails, {
        id: influencerList[idx]
      })

      // if influence trail doesn't exist in all trail response, create a object {id, title}
      if (_.isUndefined(influenceTrail)) {
        influenceTrail = {
          id: influencerList[idx]
        }
      }

      // set title for trail (used in influencerList component)
      influenceTrail.title = this.createTrailLabel(influencerList[idx])

      // during an update, the influences list may exceed previous list length
      // check if trail object exist at this current index
      // if true, merge the object
      // if false, add object
      if (this.influences[idx] && influencerList[idx] === this.influences[idx].id) {
        this.influences[idx] = _.merge(this.influences[idx], influenceTrail)
      } else {
        this.influences.splice(idx, 1, influenceTrail)
      }
    }
  }

  updateInfluencesList (currList, influencesList, allTrails) {
    for (let idx = 0; idx < influencesList.length; idx++) {
      let influenceTrail = _.find(allTrails, {
        id: influencesList[idx]
      })
      if (currList[idx] && influencesList[idx] === currList[idx].id) {
        currList[idx] = influenceTrail || influencesList[idx]
      } else {
        currList.splice(idx, 1, influenceTrail || influencesList[idx])
      }
    }
  }

  /**
   *
   * Method to update stackedIoC (IoC displayed)
   *
   */
  prepareRecommendations (trailIocInfoList) {
    const trailIdhash = this.trailIdHash
    const trailState = this.state
    if (_.isEmpty(trailIocInfoList)) {
      trailIocInfoList = _.cloneDeep(this.trailIocInfoList)
    }
    this.recommendations = _.chain(trailIocInfoList)
      .groupBy('agentId')
      .map((details, id) => {
        return {
          id: id,
          recommendations: _.chain(details)
            .reduce((recommendations, detail) => {
              if (detail.recommendation && detail.recommendation.length > 0) {
                _.each(detail.recommendation, recommendation => {
                  // don't display duplicate recommendations and invalid recommendatation that has not been acted on
                  if (recommendation.duplicate) {
                    return
                  }

                  // create new recommendation
                  let reco = new Recommendation('iocHashIndex', {
                    globalTrailIdHash: trailIdhash,
                    trailIdHash: [detail.agentId, detail.trailId].join(':'),
                    iocHash: detail.iocHash,
                    ruleId: detail.ruleid,
                    score: detail.scoreContribution,
                    index: recommendation.index,
                    valid: recommendation.valid,
                    description: recommendation.recommendation,
                    action: recommendation.action,
                    compatibleAgent: recommendation.compatibleAgent,
                    priority: recommendation.priority && recommendation.priority.toLowerCase(),
                    justification: recommendation.justification
                  })

                  // ..
                  reco.setActionMap(detail.actionMap)
                  reco.parseExecutedActions(this.markedIocs)

                  // Applies to MITIGATED progressions only
                  // Mark any valid recommendations as dimissed
                  if (trailState === 'MITIGATED' && reco.valid && _.isUndefined(reco.latestAction.status)) {
                    reco.latestAction.status = 'dismissed'
                  }
                  // Mark any failed recommendations as dimissed
                  else if (trailState === 'MITIGATED' && reco.valid && reco.latestAction.status === 'failed') {
                    reco.latestAction.status = 'dismissed'
                  }
                  // ..
                  recommendations.push(reco)
                })
              }
              return recommendations
            }, [])
            .sortBy('iocHash')
            .value()
        }
      })
      .value()
  }

  /**
   *
   * Prepare trail details (IoCs) for the trail details view.
   *
   * Column Ordering (meta info):
   * IoC Ascending order: First host entry to last/current host of the  attack trail.
   * IoC Descending order: Last/current host to first host entry of the attack trail.
   *
   *
   */
  prepareIoCAttackDetails (hostList, lateralMovements, trailIocInfoList, orderBy = OrderByEnum.ASC) {
    if (_.isEmpty(hostList)) {
      hostList = _.cloneDeep(this.hostList)
    }
    if (_.isEmpty(lateralMovements)) {
      lateralMovements = _.cloneDeep(this.lateralMovementEdges)
    }
    if (_.isEmpty(trailIocInfoList)) {
      trailIocInfoList = _.cloneDeep(this.trailIocInfoList)
    }

    // change order by direction
    this.orderByDir = orderBy

    // build attack progression host path
    this.hostPath = this.buildHostPath(hostList, lateralMovements, orderBy)

    // trailIocList has all iocs (lat movement, others)
    // combine lateral movement ioc with rest of the iocs
    this.trailIocList = this.configureTrailInfoList(lateralMovements, trailIocInfoList, orderBy)
    this.trailSummary = this.summarizeTrailInfo(this.hostPath, lateralMovements, this.trailIocList)
    this.collapsedTrailIocList = this.collapseIocs(this.trailIocList, orderBy)

    this.sortInfluencesTrail()
  }

  /**
   *
   * Update attack details
   *
   */
  updateIoCAttackDetails (hostList, lateralMovements, trailIocInfoList) {
    let isNewHosts = false

    // build hostpath list with new hosts
    // 2/8/2021: with containers introduced, build host path first
    // containers lateral movement will not be included in hostlist
    let updatedHostPathList = this.buildHostPath(hostList, lateralMovements, this.orderByDir)

    // check if host list has changed
    // if a lateral movement to a new host has occurred
    if (this.hostPath.length !== updatedHostPathList.length) {
      isNewHosts = true // mark update has a lateral movement crossed into a new host
      this.hostList = hostList
      if (this.orderByDir === OrderByEnum.ASC) {
        // iterate from end of previous hosts count to new hosts count (if lateral movement occurred)
        for (let idx = this.hostPath.length; idx < updatedHostPathList.length; idx++) {
          this.hostPath.splice(this.hostPath.length, 0, updatedHostPathList[idx])
        }
      } else {
        // for descending list, iterate after non host columns (score, seenTime)
        // insert host records
        for (let idx = trailsDetailMetaInfo.meta.columns.length; idx < updatedHostPathList.length; idx++) {
          this.hostPath.splice(idx, 1, updatedHostPathList[idx])
        }
      }
    }

    // check new iocs have been stitched to this attack trail
    if (this.trailIocList.length !== lateralMovements.length + trailIocInfoList.length) {
      let updatedIocList = this.configureTrailInfoList(lateralMovements, trailIocInfoList, this.orderByDir)

      // if order is ascending, add to the end of the list
      if (this.orderByDir === OrderByEnum.ASC) {
        for (let idx = this.trailIocList.length; idx < updatedIocList.length; idx++) {
          this.trailIocList.push(updatedIocList[idx])
        }
        // bug: cyber kill chain summary is not updating
        // update first ioc will update dwelling time summary
        _.merge(this.trailIocList[0], updatedIocList[0])
      } else {
        // if the order is descending, add new ioc to the top of the list
        // new ioc are difference between existing list and new list
        let updateIocLengthDiff = updatedIocList.length - this.trailIocList.length
        for (let idx = 0; idx < updateIocLengthDiff; idx++) {
          this.trailIocList.splice(idx, 0, updatedIocList[idx])
        }
        // on descending order - sourceAgentColIndex & targetAgentColIndex will change when new hosts appear
        // merge existing recordset with updated recordset
        if (isNewHosts) {
          for (let idx = updateIocLengthDiff; idx < updatedIocList.length; idx++) {
            _.merge(this.trailIocList[idx], updatedIocList[idx])
          }
        }
      }

      // safe to assume there is no case where a host is added without an ioc occurring
      this.lateralMovementEdges = lateralMovements
      // set original list
      this.trailIocInfoList = trailIocInfoList
      this.trailSummary = this.summarizeTrailInfo(this.hostPath, lateralMovements, this.trailIocList)

      let updatedCollapsedIocList = this.collapseIocs(updatedIocList, this.orderByDir)
      this.updateCollapsedTrailIocList(updatedCollapsedIocList, this.orderByDir, isNewHosts)
      this.sortInfluencesTrail()
    }
  }

  /**
   *
   * Build lateral movment path of host(s)/agent(s) in order of appearance and sort accordingly.
   *
   */
  buildHostPath (hostList, lateralMovements, orderBy = OrderByEnum.ASC) {
    // reset on build
    let hostPathProgression = _.merge([], trailsDetailMetaInfo.meta.columns)
    let sourceAgentStr = orderBy === OrderByEnum.ASC ? 'sourceAgent' : 'targetAgent'
    let targetAgentStr = orderBy === OrderByEnum.ASC ? 'targetAgent' : 'sourceAgent'
    let sourceContainerStr = orderBy === OrderByEnum.ASC ? 'sourceContainerId' : 'targetContainerId'
    let targetContainerStr = orderBy === OrderByEnum.ASC ? 'targetContainerId' : 'sourceContainerId'

    /**
     * If attack trail is across multiple machines
     * Lateral Movement gives us occurrence in order of first to last
     */
    if (lateralMovements && !_.isEmpty(lateralMovements)) {
      lateralMovements
        .sort((a, b) => {
          if (orderBy === OrderByEnum.ASC) {
            return dateTimeSort(a.seenTime, b.seenTime) // sort by asc (earliest to latest)
          }
          return dateTimeSort(b.seenTime, a.seenTime)
        })
        .forEach((host, idx) => {
          // add SOURCE agent to header column
          // check if the source is a container
          if (
            !_.isEmpty(host[sourceContainerStr]) &&
            !_.some(hostPathProgression, {
              label: host[sourceContainerStr]
            })
          ) {
            addIocHostColumn(
              hostPathProgression,
              host[sourceContainerStr],
              hostPathProgression.length, // substract number of right fixed columns
              this.createAgentName(host[sourceAgentStr])
            )
          } else if (
            !_.some(hostPathProgression, {
              label: host[sourceContainerStr]
            }) &&
            !_.some(hostPathProgression, {
              label: this.createAgentName(host[sourceAgentStr])
            })
          ) {
            addIocHostColumn(
              hostPathProgression,
              this.createAgentName(host[sourceAgentStr]),
              hostPathProgression.length // substract number of right fixed columns
            )
          }

          // add TARGET agent to header column
          // check if the target is a container
          if (
            !_.isEmpty(host[targetContainerStr]) &&
            !_.some(hostPathProgression, {
              label: host[targetContainerStr]
            })
          ) {
            addIocHostColumn(
              hostPathProgression,
              host[targetContainerStr],
              hostPathProgression.length, // substract number of right fixed columns
              this.createAgentName(host[targetAgentStr])
            )
          } else if (
            !_.some(hostPathProgression, {
              label: host[targetContainerStr]
            }) &&
            !_.some(hostPathProgression, {
              label: this.createAgentName(host[targetAgentStr])
            })
          ) {
            addIocHostColumn(
              hostPathProgression,
              this.createAgentName(host[targetAgentStr]),
              hostPathProgression.length // substract number of right fixed columns
            )
          }
        })
    } else {
      hostList.forEach((host, idx) => {
        addIocHostColumn(
          hostPathProgression,
          this.createAgentName(host),
          hostPathProgression.length // substract number of right fixed columns
        )
      })
    }

    return hostPathProgression
  }

  /**
   *
   * Combine lateral movements with trail ioc list.
   * Configure need properties for ioc trail table.
   * Configure collapse IoC list (Iterate list once).
   *
   */
  configureTrailInfoList (lateralMovements, trailIocInfoList, orderBy) {
    let singleHostContainerNoLateralMovement = this.hostList.length === 1 && lateralMovements.length === 0
    let isContainerIdSet = false

    // reset count of influenceby trails
    this.influencedBy.forEach(influenceTrail => {
      influenceTrail.count = 0
    })

    return _.chain(trailIocInfoList)
      .concat(lateralMovements)
      .sort((a, b) => {
        if (orderBy === OrderByEnum.ASC) {
          return dateTimeSort(a.seenTime, b.seenTime) // sort by asc (earliest to latest)
        }
        return dateTimeSort(b.seenTime, a.seenTime)
      })
      .map((ioc, idx, arr) => {
        if (!moment(ioc.seenTime).isValid()) {
          ioc.seenTime = ioc.seenTime * NANOSECONDS_TO_MILLISECONDS
        }

        // UI-184: Unable to unpack ML detections with multiple occurrences.
        // Set to 1 until we find a better to decorate ML detections.
        ioc.occurrence = 1

        // globalTrailIdHash are for lateral movement IoCs
        // lateral movements receive a score of 0, contains source and target agent
        if (ioc.globalTrailIdHash) {
          ioc.lateralmove = true
          ioc.scoreContribution = 0
          if (!_.isEmpty(ioc.sourceContainerId)) {
            ioc.sourceAgentLabel = this.createAgentName(ioc.sourceContainerId)
          } else {
            ioc.sourceAgentLabel = this.createAgentName(ioc.sourceAgent)
          }
          if (!_.isEmpty(ioc.targetContainerId)) {
            ioc.targetAgentLabel = this.createAgentName(ioc.targetContainerId)
          } else {
            ioc.targetAgentLabel = this.createAgentName(ioc.targetAgent)
          }
          // json parse eventslist
          ioc.iocEventsList = ioc.lmEventList

          // check if iocEventsList is an array (ioc was fired from path or multi-cluster rule)
          if (_.isArray(ioc.iocEventsList)) {
            ioc.multiEventList = true
            ioc.iocEventsList.forEach((iocEvent, idx) => {
              // iterate through each event list item and replace with json parse data of same item
              ioc.iocEventsList.splice(idx, 1, ioc.iocEventsList[idx])
            })
            ioc.iocEventsList.sort((a, b) => {
              // sort by asc (earliest to latest)
              return dateTimeSort(a.timestamp, b.timestamp)
            })
          }

          let colIdx = _.find(this.hostPath, {
            label: ioc.sourceAgentLabel
          })
          if (colIdx && colIdx.positionIndex > -1) {
            ioc.sourceAgentColIndex = colIdx.positionIndex
          }

          colIdx = _.find(this.hostPath, {
            label: ioc.targetAgentLabel
          })
          if (colIdx && colIdx.positionIndex > -1) {
            ioc.targetAgentColIndex = colIdx.positionIndex
          }
        } else {
          // other IoCs
          // only have source agent, no target agent
          ioc.lateralmove = false

          // json parse eventslist
          ioc.iocEventsList = ioc.iocEventsList

          // check if iocEventsList is an array (ioc was fired from path or multi-cluster rule)
          if (_.isArray(ioc.iocEventsList)) {
            ioc.multiEventList = true
            ioc.iocEventsList.forEach((iocEvent, idx) => {
              // iterate through each event list item and replace with json parse data of same item
              ioc.iocEventsList.splice(idx, 1, ioc.iocEventsList[idx])
            })
            ioc.iocEventsList.sort((a, b) => {
              // sort by asc (earliest to latest)
              return dateTimeSort(a.timestamp, b.timestamp)
            })
          }

          // compile list of influencedBy trails
          if (ioc.iocSummary.includes('influencerTrails')) {
            ioc.iocDetail = ioc.iocDetail
            if (_.isUndefined(ioc.influencedBy)) {
              ioc.influencedBy = []
            }
            // influenced by is a list
            for (let idx = 0; idx < ioc.iocDetail.length; idx++) {
              let influencedBy = this.addInfluencedByTrail({
                id: ioc.iocDetail[idx],
                title: this.createTrailLabel(ioc.iocDetail[idx])
              })

              // used to create class on ioc trail table
              // class will be used by influencerlist to comb through and highlight rows
              ioc.influencedBy.push({
                count: influencedBy.count,
                influencer: this.createTrailLabel(ioc.iocDetail[idx]).replace(/[:.]/g, '_')
              })
            }
          }

          //  Use Case: a trailblazer agent starts after a long running container
          //  IoCs will appear for the container but there are no lateral movements.
          //  Our host column will not pick up the containerId as lateral movements is 0.
          //  When pinning an IoC to a column, we are able to match bc IoC contains containerId
          //  and that's what we use to target a column.

          //  solution: update hostpath[0] label and parentHeader for agentId and containerId
          if (singleHostContainerNoLateralMovement && !isContainerIdSet) {
            if (!_.isEmpty(ioc.containerId)) {
              this.hostPath[0].parentHeader = this.hostPath[0].label
              this.hostPath[0].label = ioc.containerId
              isContainerIdSet = true
            }
          }

          if (_.isEmpty(ioc.containerId)) {
            ioc.agentIdLabel = this.createAgentName(ioc.agentId)
          } else {
            ioc.agentIdLabel = ioc.containerId
          }

          let colIdx = _.find(this.hostPath, {
            label: ioc.agentIdLabel
          })
          if (colIdx && colIdx.positionIndex > -1) {
            ioc.agentColIndex = colIdx.positionIndex
          }
        }

        if (isNaN(parseInt(ioc.scoreContribution))) {
          ioc.scoreContribution = 0
        }

        ioc.riskLevel = ioc.scoreContribution ? getDetectionRiskLevel(ioc.scoreContribution) : 'none'

        if (idx === 0) {
          ioc.scoreAccumulation = ioc.scoreContribution
        } else if (ioc.lateralmove) {
          ioc.scoreAccumulation = arr[idx - 1]['scoreAccumulation']
        } else {
          if (!ioc.scoreContribution) {
            ioc.scoreContribution = 0
          }
          ioc.scoreAccumulation = arr[idx - 1]['scoreAccumulation'] + ioc.scoreContribution
        }

        ioc.seenTimeAgoFormatted = formatFromNow(ioc.seenTime, {
          seconds: false
        })

        return ioc
      })
      .value()
  }

  /**
   *
   * Summarize trail info.
   *
   */
  summarizeTrailInfo (hostList, lateralMovements, trailIocList) {
    const firstIoC = trailIocList[0]
    const lastIoC = trailIocList[trailIocList.length - 1]
    const isAscOrder = this.orderByDir === OrderByEnum.ASC
    const seenTime = isAscOrder ? firstIoC.seenTime : lastIoC.seenTime
    const seenTimeAgoFormatted = isAscOrder ? firstIoC.seenTimeAgoFormatted : lastIoC.seenTimeAgoFormatted
    const centerDonutTitle = moment(seenTime).fromNow(true).split(' ')

    const allAttackChainExec = Array.from(new Set(trailIocList.map(tactic => tactic.iocTactic || '')))
      .filter(tactic => tactic !== '')
      .map(tactic => tactic.replace(/ /g, '_'))

    const allTactics = buildAttackChainCommand(allAttackChainExec)

    let trailSummary = {
      progressionSeverity: this.progressionSeverity,
      score: lastIoC['scoreAccumulation'],
      iocs: trailIocList.length - (lateralMovements ? lateralMovements.length : 0),
      hosts: hostList.length - trailsDetailMetaInfo.meta.columns.length,
      lateralmoves: lateralMovements ? lateralMovements.length : 0,
      attackChain: {
        seenTime: seenTime,
        seenTimeAgoFormatted: seenTimeAgoFormatted,
        data: {
          centerDonutTitle: centerDonutTitle,
          datasets: [
            {
              data: allTactics['data'],
              backgroundColor: allTactics['backgroundColor']
            }
          ],
          labels: allTactics['labels']
        }
      }
    }

    const sliceStart = Math.max(trailSummary.iocs - TRAILSCOREHISTORY_SIZE, 0)
    const slicedTrailIocList = trailIocList.slice(sliceStart, trailSummary.iocs + 1)

    trailSummary.scoreContributionHistory = {
      labels: slicedTrailIocList.map(point => point.seenTime),
      datasets: [
        {
          data: runningScoreContribution(slicedTrailIocList, 'scoreContribution')
        }
      ]
    }

    return trailSummary
  }

  /**
   *
   * Method to collapse trailIocList. Applying the following logic:
   * 1. We collapse any repeating IOC (based on rule ID),
   * show the most recent data for the panel on the right,
   * and aggregate the overall score.
   * There are some scoring caveats that are addressed by backend
   * 2. We have a Global Expand / Collapse button on top somewhere (collapsed by default),
   * where the current view (every single IOC) is available to view.
   * this will be helpful for debugging purposes.
   * 3. We have agreed to stay away from collapsing at a speciful Rule (that repeats) level,
   * as that may impose significant UI complexities once the attack moves to multiple machines.
   *
   */
  collapseIocs (iocList = [], orderBy = OrderByEnum.ASC) {
    let collapsedList = []
    let prevIoc = null
    for (let ioc of iocList) {
      let copyIoc = { ...ioc }
      if (!copyIoc.iocHashList) {
        copyIoc.iocHashList = [copyIoc.iocHash]
      }
      if (!prevIoc) {
        collapsedList.push(copyIoc)
        prevIoc = copyIoc
        continue
      }
      if (this.lateralMovementEquals(copyIoc, prevIoc) || this.detectionEquals(copyIoc, prevIoc)) {
        this.mergeCollapsedIoC(copyIoc, prevIoc, orderBy)
      } else {
        copyIoc.occurrenceEndTime = copyIoc.seenTime
        collapsedList.push(copyIoc)
        prevIoc = copyIoc
      }
    }
    return collapsedList
  }

  /**
   * Method to update collapsedTrailIocList with updated collpaseIoCList
   *
   * @param {*} newIocList
   * @param {*} orderBy
   * @param {*} updateHosts
   */
  updateCollapsedTrailIocList (newIocList, orderBy = OrderByEnum.ASC, updateHosts = false) {
    let updateIocLengthDiff = newIocList.length - this.collapsedTrailIocList.length

    switch (orderBy) {
      case OrderByEnum.DESC:
        // desc order - merge first ioc
        // case: new detections are repeat of last known detection on the trail,
        //       updating the first ioc (latest in desc sort) with new occurrence, seentime, occurrenceEndtime
        if (updateIocLengthDiff === 0) {
          _.merge(this.collapsedTrailIocList[0], newIocList[0])
          break
        }

        // descending order - iterate from top
        for (let idx = updateIocLengthDiff - 1; idx >= 0; idx--) {
          this.checkAddCollapseTrailIocList(newIocList[idx], orderBy)
        }

        // when new lateral movement occurs, new host added to top of stack
        // so we must update host's head and tail index for html rendering
        if (updateHosts) {
          for (let idx = updateIocLengthDiff; idx < newIocList.length; idx++) {
            _.merge(this.collapsedTrailIocList[idx], newIocList[idx])
          }
        }
        break
      default:
        // asc order - merge last ioc
        // case: new detections are repeat of last known detection on the trail,
        //       updating the last ioc (latest in asc sort) with new occurrence, seentime, occurrenceEndtime
        if (updateIocLengthDiff === 0) {
          _.merge(this.collapsedTrailIocList[newIocList.length - 1], newIocList[newIocList.length - 1])
          break
        }
        // ascending order - iterate from bottom
        for (let idx = this.collapsedTrailIocList.length; idx < newIocList.length; idx++) {
          this.checkAddCollapseTrailIocList(newIocList[idx], orderBy)
        }
    }
  }

  /**
   * Method to check new IoC detection against the last IoC detection in the trail.
   * If they are equal, collapse new IoC to the last IoC in the list by updating
   * seenTime, occurrenceEndTime, occurrence count, iocEventsList, and recommendation.
   * If they are not equal, add to the collapsedTrailIocList.
   *
   * @param {*} newIoc
   * @param {*} orderBy
   */
  checkAddCollapseTrailIocList (newIoc, orderBy = OrderByEnum.ASC) {
    let copyIoc = _.cloneDeep(newIoc)
    if (_.isUndefined(copyIoc.iocHashList)) {
      copyIoc.iocHashList = [copyIoc.iocHash]
    }
    let lastIoc = this.collapsedTrailIocList[this.collapsedTrailIocList.length - 1]

    if (orderBy === OrderByEnum.DESC) {
      lastIoc = this.collapsedTrailIocList[0]
    }
    // check current IoC vs previous IoC
    // if yes, fold/stack new ioc into prev ioc
    if (this.lateralMovementEquals(copyIoc, lastIoc) || this.detectionEquals(copyIoc, lastIoc)) {
      // show the most recent data for the panel on the right
      this.mergeCollapsedIoC(copyIoc, lastIoc, orderBy)
    } else {
      copyIoc.occurrenceEndTime = copyIoc.seenTime
      // if ASC, append, else prepend
      if (orderBy === OrderByEnum.ASC) {
        this.collapsedTrailIocList.push(copyIoc)
      } else {
        this.collapsedTrailIocList.splice(0, 0, copyIoc)
      }
    }
  }

  /**
   * Method to update stackedIoC (IoC displayed)
   *
   * @param {*} currentIoc
   * @param {*} stackedIoc
   * @param {*} orderBy
   */
  mergeCollapsedIoC (currentIoc, stackedIoc, orderBy = OrderByEnum.ASC) {
    if (_.isUndefined(stackedIoc.iocHashList)) {
      stackedIoc.iocHashList = [stackedIoc.iocHash]
    }
    stackedIoc.iocHashList.push(currentIoc.iocHash)
    switch (orderBy) {
      case OrderByEnum.DESC:
        _.merge(stackedIoc, {
          seenTime: stackedIoc.seenTime,
          occurrenceEndTime: currentIoc.seenTime,
          occurrence: stackedIoc.occurrence + 1
        })
        break
      default:
        _.merge(stackedIoc, {
          iocEventsList: currentIoc.iocEventsList,
          recommendation: currentIoc.recommendation,
          seenTime: stackedIoc.seenTime,
          occurrenceEndTime: currentIoc.seenTime,
          occurrence: stackedIoc.occurrence + 1
        })
    }
  }
}



// WEBPACK FOOTER //
// ./src/resources/models/Trail.js